import Highcharts from 'highcharts';

import { InsightsChartSpec } from 'services/streamInsightsQuery';

import {
  baseInsightsOptions,
  formatChartValue,
  tooltipHtml,
  tooltipShell,
} from './shared';

// scatter | bubble — one point per breakdown value, with each axis fed by a
// different metric. series[0] is X, series[1] is Y, and bubble's optional
// series[2] sizes the point. `spec.categories[i]` names point i; it is NOT an
// axis tick, so we must not set xAxis.categories (that would relabel the value
// axis with breakdown members and mis-plot every point).
//
// The backend inner-joins the series before sending, so every index has a real
// number on every axis.
const buildXyOptions = (spec: InsightsChartSpec): Highcharts.Options => {
  const { chartType, formatting } = spec;
  const [xSeries, ySeries, sizeSeries] = spec.series;

  const points = spec.categories.map((name, index) => ({
    name,
    x: xSeries?.data[index] ?? 0,
    y: ySeries?.data[index] ?? 0,
    // Uniform size until a third (size) metric is configured — same fallback
    // the saved bubble gadget uses.
    ...(chartType === 'bubble'
      ? { z: sizeSeries ? sizeSeries.data[index] ?? 0 : 1 }
      : {}),
  }));

  const base = baseInsightsOptions({
    spec,
    chartType,
    usePointName: true,
    // One series of points; the legend would just repeat the chart title.
    showLegend: false,
  });

  return {
    ...base,
    chart: { ...base.chart, zooming: { type: 'xy' } },
    series: [
      { type: chartType, name: spec.title, data: points },
    ] as unknown as Highcharts.SeriesOptionsType[],
    tooltip: {
      ...tooltipShell,
      formatter(this: Highcharts.TooltipFormatterContextObject): string {
        const x = formatChartValue(Number(this.x), formatting);
        const y = formatChartValue(Number(this.y), formatting);
        const size =
          chartType === 'bubble' && sizeSeries
            ? ` · ${sizeSeries.name}: ${formatChartValue(
                Number(
                  (this.point as Highcharts.Point & { z?: number }).z ?? 0,
                ),
                formatting,
              )}`
            : '';
        return tooltipHtml(
          `<b>${this.point?.name ?? ''}</b><br/>` +
            `${spec.categoryLabel ?? 'x'}: ${x}<br/>` +
            `${spec.valueLabel ?? 'y'}: ${y}${size}`,
        );
      },
    },
    xAxis: {
      ...(spec.categoryLabel ? { title: { text: spec.categoryLabel } } : {}),
      labels: {
        formatter() {
          return formatChartValue(Number(this.value), formatting);
        },
      },
    },
    yAxis: {
      ...(spec.valueLabel ? { title: { text: spec.valueLabel } } : {}),
      labels: {
        formatter() {
          return formatChartValue(Number(this.value), formatting);
        },
      },
    },
  };
};

export default buildXyOptions;
